import { Button, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Tooltip } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowRightRegular } from '@fluentui/react-icons';
import { createColumnHelper, flexRender, useTable } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PermissionChangeRequest,
  FolderPermissionLevel,
  FolderPermissionResponse,
  FolderPermissionsResponse,
} from '../../../api/types.gen';
import { ActionsCell } from './PermissionInfoTable/ActionsCell';
import { CreatePermissionCell } from './PermissionInfoTable/CreatePermissionCell';
import { EditPermissionCell } from './PermissionInfoTable/EditPermissionCell';
import { FolderPermissionCell } from './PermissionInfoTable/FolderPermissionCell';
import { HeaderCell } from './PermissionInfoTable/HeaderCell';
import {
  getCreatePermissionLevel,
  getEditPermissionLevel,
  getFolderPermissionLevel,
  getReadPermissionLevel,
} from './PermissionInfoTable/PermissionEnums';
import { permissionTableFeatures, PermissionTableFeatures } from './PermissionInfoTable/permissionTableFeatures';
import { ReadPermissionCell } from './PermissionInfoTable/ReadPermissionCell';

interface IFolderPermissionsInfoTableProps {
  folderInfo: FolderPermissionsResponse;
  handleEdit: (permissionInfo: PermissionChangeRequest, applyRecursive: boolean) => Promise<void>;
  handleDelete: (permissionInfo: FolderPermissionResponse, applyRecursive: boolean) => Promise<void>;
  isLoading: boolean;
}

export default function FolderPermissionInfoTable({ folderInfo, handleEdit, handleDelete, isLoading }: IFolderPermissionsInfoTableProps) {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const permissions: FolderPermissionResponse[] = folderInfo.permissions ?? [];

  const columnHelper = createColumnHelper<PermissionTableFeatures, FolderPermissionResponse>();

  const columns = columnHelper.columns([
    columnHelper.accessor('user', {
      header: (header) => <HeaderCell headerContext={header} />,
    }),
    columnHelper.accessor('permissionLevel', {
      header: (header) => <HeaderCell headerContext={header} />,
    }),
    columnHelper.accessor((row) => getCreatePermissionLevel(row.canCreateItems ?? false, row.canCreateSubFolders ?? false), {
      id: 'createPermissions',
      header: (header) => <HeaderCell headerContext={header} />,
      cell: (cellContext) => <CreatePermissionCell cellContext={cellContext} />,
    }),
    columnHelper.accessor((row) => getReadPermissionLevel(row.isFolderVisible ?? false), {
      id: 'readPermissions',
      header: (header) => <HeaderCell headerContext={header} />,
      cell: (cellContext) => <ReadPermissionCell cellContext={cellContext} />,
    }),
    columnHelper.accessor((row) => getEditPermissionLevel(row.editItems), {
      id: 'editPermissions',
      header: (header) => <HeaderCell headerContext={header} />,
      cell: (cellContext) => <EditPermissionCell cellContext={cellContext} />,
    }),
    columnHelper.accessor((row) => getEditPermissionLevel(row.deleteItems), {
      id: 'deletePermissions',
      header: (header) => <HeaderCell headerContext={header} />,
      cell: (cellContext) => <EditPermissionCell cellContext={cellContext} />,
    }),
    columnHelper.accessor(
      (row) => getFolderPermissionLevel(row.isFolderOwner ?? false, row.isFolderVisible ?? false, row.isFolderContact ?? false),
      {
        id: 'folderPermissions',
        header: (header) => <HeaderCell headerContext={header} />,
        cell: (cellContext) => <FolderPermissionCell cellContext={cellContext} />,
      },
    ),

    columnHelper.display({
      id: 'actions',
      cell: (cellContext) => (
        <ActionsCell
          cellContext={cellContext}
          handleDelete={(_, applyRecursive) => handleDelete(cellContext.row.original, applyRecursive)}
          handleEdit={handleEdit}
          folderInfo={folderInfo}
          isLoading={isLoading}
        />
      ),
    }),
  ]);

  const table = useTable({
    features: permissionTableFeatures,
    data: permissions,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  const permissionValues: Array<{
    text: string;
    value: FolderPermissionLevel;
  }> = [
    {
      text: t('permissions.modifyAction.levelGroup.options.owner'),
      value: FolderPermissionLevel.OWNER,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.publishingEditor'),
      value: FolderPermissionLevel.PUBLISHING_EDITOR,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.editor'),
      value: FolderPermissionLevel.EDITOR,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.publishingAuthor'),
      value: FolderPermissionLevel.PUBLISHING_AUTHOR,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.author'),
      value: FolderPermissionLevel.AUTHOR,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.noneditingAuthor'),
      value: FolderPermissionLevel.NONEDITING_AUTHOR,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.reviewer'),
      value: FolderPermissionLevel.REVIEWER,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.contributor'),
      value: FolderPermissionLevel.CONTRIBUTOR,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.none'),
      value: FolderPermissionLevel.NONE,
    },
    {
      text: t('permissions.modifyAction.levelGroup.options.custom'),
      value: FolderPermissionLevel.CUSTOM,
    },
  ];

  const renderPermissionLevel = (permissionLevel: string) => {
    return permissionValues.find((permission) => permission.value === permissionLevel)?.text;
  };

  return (
    <>
      <div>
        <Table style={{ tableLayout: 'auto' }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeaderCell key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHeaderCell>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getAllCells().map((cell) => (
                  <TableCell key={cell.id} style={{ whiteSpace: 'nowrap' }}>
                    {cell.id.includes('permissionLevel')
                      ? renderPermissionLevel(cell.getValue() as string)
                      : flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Tooltip content={t('permissions.pagination.previousLabel')} relationship="label">
          <Button
            appearance="transparent"
            size="medium"
            icon={<ArrowLeftRegular />}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          />
        </Tooltip>
        <span>
          {t('permissions.pagination.currentLabel')}
          <strong>
            {pagination.pageIndex + 1} / {table.getPageCount()}
          </strong>
        </span>
        <Tooltip content={t('permissions.pagination.nextLabel')} relationship="label">
          <Button
            appearance="transparent"
            size="medium"
            icon={<ArrowRightRegular />}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          />
        </Tooltip>
      </div>
    </>
  );
}
